import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DialogTrigger from '../../../components/dialog-trigger';
import QuestionIcon from './assets/questionMarkCircle.svg?react';
import GeneralTooltip from '../../../components/general-tooltip';
import { Body } from '../../../components/typography';
import { SOURCES } from '../../../constants/sources';
import { VIRTUAL_CLASS_URL } from '../../../config';
import { getUnitutorTotalScore } from '../../../lib/correction';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;

    font-size: 16px;
    letter-spacing: 0;
    line-height: 24px;
`;

const Row = styled.div`
    display: flex;
    align-items: center;

    & > *:not(:last-child) {
        margin-right: 8px;
    }

    & svg {
        width: 18px;
        height: 18px;
        fill: ${(props) => props.theme.colors['myzanichelli-blue-main']};
    }
`;

const TooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    width: 256px;
`;

const VoteTooltipContent = styled(TooltipContent)`
    padding: 2px 0;
    width: 240px;

    & .voteModifiedTooltip > div {
        margin: calc(var(--space-unit) / 2) 0;
    }
`;

const Link = styled.a`
    color: var(--color-white);
    text-decoration: underline;
`;

export const PointsTooltip = () => {
    return (
        <TooltipContent>
            <Body lv={5} color="white">
                <div>
                    <strong>{'Il primo numero'}</strong>
                    {' è la somma dei punti totalizzati. '}
                    <strong>{'Il secondo numero'}</strong>
                    {' è il punteggio massimo per questa prova.'}
                </div>
                {'Ricorda: gli esercizi Risposta Aperta sono esclusi dalla somma; gli esercizi non svolti valgono 0 punti.'}
            </Body>
        </TooltipContent>
    );
};

export const VoteTooltip = () => {
    return (
        <TooltipContent>
            <Body lv={5} color="white">
                <div>
                    {'Il '}
                    <strong>{'voto'}</strong> {'si calcola a partire dal punteggio ottenuto, così:'}
                </div>
                {'(punti totalizzati : punti totali) x 10'}
                {'Esempio: (20 : 24) x 10 = 8.3'}
            </Body>
        </TooltipContent>
    );
};

export const getVirtualClassUrl = (assignment, userAllowed, source) => {
    let classroomUrl = VIRTUAL_CLASS_URL;
    if (assignment.id) {
        classroomUrl += `/classroom/${assignment.classId}/registro/`;
        if (userAllowed) {
            classroomUrl += `assignment/${assignment.id}`;
        } else {
            classroomUrl += 'activities';
        }
    }
    return `${classroomUrl}?utm_source=${source}`;
};

export const VoteModifiedTooltip = ({ assignment, userAllowed, originalScore, source }) => {
    let classroomUrl = getVirtualClassUrl(assignment, userAllowed, source);
    const fractionDigits = source === SOURCES.ZTE_UNITUTOR ? 2 : 1;

    return (
        <VoteTooltipContent>
            <Body level={5} color="white" className="voteModifiedTooltip">
                <Body level={5} color="white" weight="semi-bold">
                    {'VOTO MODIFICATO'}
                </Body>
                <div>
                    <Body level={5} color="white" weight="semi-bold" as="span">
                        {'Voto iniziale: '}
                    </Body>
                    {originalScore.toFixed(fractionDigits)}
                </div>
                <div>
                    {'Vuoi maggiori dettagli su questa prova?'}{' '}
                    <Link target="_blank" href={classroomUrl}>
                        {'Vai su Classi Virtuali'}
                    </Link>
                </div>
                <div>
                    {'Vuoi capire meglio come funzionano voto e punteggio? '}
                    <Link target="_blank" href="https://assistenza.zanichelli.it/support/solutions/folders/4000035503">
                        {'Leggi le domande frequenti'}
                    </Link>
                </div>
            </Body>
        </VoteTooltipContent>
    );
};

const ScoreSection = ({ source, correctionPoints, exercises, scoreModifier, remoteScore, groupCorrectionByTopic }) => {
    if (!source || source === SOURCES.TT || groupCorrectionByTopic) {
        return <></>;
    }

    if (source === SOURCES.ZTE_UNITUTOR) {
        const totalScore = exercises.length * scoreModifier.correct;
        const score = remoteScore ? remoteScore : getUnitutorTotalScore(correctionPoints, scoreModifier);

        return (
            <Container>
                <Row>
                    <Body lv={3} color="gray-800">
                        {'Punteggio: '}
                        <div>
                            <strong>{score.toFixed(2)}</strong>
                            {`/${totalScore}`}
                        </div>
                    </Body>
                    <DialogTrigger>
                        <QuestionIcon />
                        <GeneralTooltip position="bottom">
                            <PointsTooltip />
                        </GeneralTooltip>
                    </DialogTrigger>
                </Row>
            </Container>
        );
    }

    const totalScore = exercises.reduce((accumulator, exercise) => {
        return isNaN(exercise.difficulty) ? accumulator : accumulator + exercise.difficulty;
    }, 0);

    const score = correctionPoints.reduce((accumulator, correction) => {
        const exercise = exercises.find((e) => e.id === correction.exerciseId);
        const difficulty = exercise.difficulty ? exercise.difficulty : 0;
        const value = correction.totalAnswers === 0 ? 0 : correction.correctAnswers / correction.totalAnswers;
        return accumulator + value * difficulty;
    }, 0);

    const vote = totalScore !== 0 ? ((score / totalScore) * 10).toFixed(1) : '-';

    return (
        <Container>
            <Row>
                <Body lv={3}>
                    {`Punteggio: `}
                    <div>
                        <strong>{score.toFixed(2)}</strong>
                        {`/${totalScore}`}
                    </div>
                </Body>
                <DialogTrigger>
                    <QuestionIcon />
                    <GeneralTooltip position="bottom">
                        <PointsTooltip />
                    </GeneralTooltip>
                </DialogTrigger>
            </Row>
            <Row>
                <Body lv={3}>
                    {'Voto in decimi: '}
                    <strong>{vote}</strong>
                </Body>
                <DialogTrigger>
                    <QuestionIcon />
                    <GeneralTooltip position="bottom">
                        <VoteTooltip />
                    </GeneralTooltip>
                </DialogTrigger>
            </Row>
        </Container>
    );
};

ScoreSection.propTypes = {
    source: PropTypes.string,
    correctionPoints: PropTypes.array,
    exercises: PropTypes.array,
    scoreModifier: PropTypes.object,
    remoteScore: PropTypes.number,
    groupCorrectionByTopic: PropTypes.bool,
};

VoteModifiedTooltip.propTypes = {
    assignment: PropTypes.object,
    userAllowed: PropTypes.bool,
    originalScore: PropTypes.number,
    source: PropTypes.string,
};

const mapStateToProps = (state) => {
    return {
        source: state.application.source,
        correctionPoints: state.status.correctionPoints,
        exercises: state.homework.exercises,
        scoreModifier: state.status.scoreModifier,
        remoteScore: state.status.score,
        groupCorrectionByTopic: state.application.groupCorrectionByTopic,
    };
};

export default connect(mapStateToProps)(ScoreSection);
